import { useState, useEffect, useCallback } from "react";
import {
  cancelAllRuns,
  cancelRun,
  getFlows,
  getLlmMessageLog,
  getLlmMessageLogs,
} from "../api";
import {
  Stack,
  Loader,
  Container,
  NumberFormatter,
  Table,
  Badge,
  Collapse,
  Modal,
  Group,
  Paper,
  TagsInput,
  Button,
  Switch,
  Tooltip,
  Text,
  Select,
  ActionIcon,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { DateTimePicker } from "@mantine/dates";
import InvocationLogDetails from "./InvocationLogDetails";
import LogDetailHeader from "./LogDetailHeader";
import { useDisclosure } from "@mantine/hooks";
import ReactTimeago from "react-timeago-i18n";
import { useLocation } from "wouter";
import StatusBadge from "./StatusBadge";
import ProgressBadge from "./ProgressBadge";
import useIsMobile from "../_Common/useIsMobile";
import {
  IconHierarchy3,
  IconFilter,
  IconThumbUp,
  IconThumbDown,
  IconPlayerStopFilled,
} from "@tabler/icons-react";

function NameBadge({ name }) {
  if (!name) return null;
  return (
    <Badge variant="light" size="sm" radius="xs">
      {name}
    </Badge>
  );
}

function FeedbackIcon({ score }) {
  if (score === 1)
    return <IconThumbUp size={14} color="var(--mantine-color-green-6)" />;
  if (score === -1)
    return <IconThumbDown size={14} color="var(--mantine-color-red-6)" />;
  return null;
}

function HierarchyBadge({ log, onNavigateToParent }) {
  if (log.agentDepth === 0) return null;

  return (
    <Tooltip
      label={
        <Stack gap={2}>
          <Text size="xs">Sub-agent at depth {log.agentDepth}</Text>
          {log.agentRole && <Text size="xs">Role: {log.agentRole}</Text>}
          {log.parentRequestId && (
            <Text size="xs" c="dimmed">Click to view parent</Text>
          )}
        </Stack>
      }
    >
      <Badge
        variant="outline"
        size="xs"
        color="orange"
        leftSection={<IconHierarchy3 size={10} />}
        style={{ cursor: log.parentRequestId ? "pointer" : "default" }}
        onClick={(e) => {
          if (log.parentRequestId) {
            e.stopPropagation();
            onNavigateToParent(log.parentRequestId);
          }
        }}
      >
        L{log.agentDepth}
        {log.agentRole ? `: ${log.agentRole}` : ""}
      </Badge>
    </Tooltip>
  );
}

/**
 * One run, as a card. The nine-column table is the right shape on a desktop and an unreadable
 * one at 390px — every column past "Status" was off-screen, and the two that survived were
 * squeezed to "L…" and "S..".
 *
 * The card keeps what a health check actually needs: which flow, how it ended, whether anything
 * went wrong, and what it cost. Model, environment and call count ride along as one dimmed line;
 * everything else is a tap away in the detail view.
 *
 * The warning count is spelled out rather than left to the marker's tooltip — there is no hover
 * on a touch screen, so a tooltip-only signal is no signal at all.
 */
/** A run that is still executing, and so can still be stopped. */
const isRunning = (log) => log.status?.toLowerCase() === "pending";

/**
 * The stop control. Rendered only for runs still in flight, and disabled once a stop has been
 * asked for — pressing it again would signal a run that is already on its way down and report a
 * second "stopping" for the same action.
 */
function StopRunButton({ log, onStop, size = "sm" }) {
  if (!isRunning(log)) return null;
  const asked = !!log.cancelRequestedAt;
  return (
    <Tooltip label={asked ? "Already stopping" : "Stop this run and its sub-agents"} withArrow>
      <ActionIcon
        size={size}
        color="red"
        variant="subtle"
        disabled={asked}
        onClick={(e) => {
          // The row navigates on click; without this, stopping also opens the run.
          e.stopPropagation();
          onStop(log);
        }}
        aria-label="Stop run"
      >
        <IconPlayerStopFilled size={size === "sm" ? 14 : 12} />
      </ActionIcon>
    </Tooltip>
  );
}

function LogCard({ log, onOpen, onNavigateToParent, onStop }) {
  const credits = log.childAgentCreditsUsed > 0 ? log.totalTreeCredits : log.cost;
  const callCount = log.stats?.totalResponses;

  return (
    <Paper
      withBorder
      radius="sm"
      p="xs"
      onClick={onOpen}
      style={{ cursor: "pointer" }}
    >
      <Group justify="space-between" wrap="nowrap" gap="xs" align="flex-start">
        <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
          <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
            <FeedbackIcon score={log.feedbackScore} />
            <Text fz="sm" fw={500} truncate style={{ minWidth: 0 }}>
              {log.flowName || log.agentRole || "Request"}
            </Text>
            <HierarchyBadge log={log} onNavigateToParent={onNavigateToParent} />
          </Group>
          <Group gap={6} wrap="wrap">
            <StatusBadge
              size="xs"
              status={log.status}
              cancelledAt={log.cancelledAt}
              cancelRequestedAt={log.cancelRequestedAt}
              failureReason={log.failureReason}
            />
            <StopRunButton log={log} onStop={onStop} size="xs" />
            <ProgressBadge
              size="xs"
              score={log.progressScore}
              certainty={log.progressCertainty}
              lowest={log.minProgressScore}
              readings={log.progressScoreCount}
              verifier={log.verifierModel}
            />
            {log.warningCount > 0 && (
              <Badge size="xs" color="yellow" variant="light" radius="sm">
                {log.warningCount} warning{log.warningCount === 1 ? "" : "s"}
              </Badge>
            )}
            {log.environment && (
              <Badge size="xs" variant="default" radius="sm" tt="none">
                {log.environment}
              </Badge>
            )}
            <Text fz="xs" c="dimmed">
              <ReactTimeago date={log.sent} />
            </Text>
          </Group>
          <Text fz="xs" c="dimmed" truncate style={{ minWidth: 0 }}>
            {log.model}
            {callCount > 0 && ` · ${callCount} call${callCount === 1 ? "" : "s"}`}
          </Text>
        </Stack>
        <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
          <Text fz="sm" fw={500}>
            <NumberFormatter
              suffix="¢"
              decimalScale={2}
              value={credits / 10000}
            />
          </Text>
          <Text fz="xs" c="dimmed">
            <NumberFormatter
              thousandSeparator
              value={log.milliseconds}
              suffix=" ms"
            />
          </Text>
        </Stack>
      </Group>
    </Paper>
  );
}

// Wouter's useLocation drops the query string, so read it off the URL directly. The lazy
// initialiser runs before the first render, so the initial fetch already carries these filters.
function useInitialFiltersFromUrl() {
  const [initial] = useState(() => {
    const search = new URLSearchParams(window.location.search);
    return {
      tags: search.getAll("tags"),
      flows: search.getAll("flows"),
      // Lets the dashboard's in-flight counter link straight to the runs it is counting —
      // /logs?statuses=pending&rootOnly=false.
      statuses: search.getAll("statuses"),
      // Sub-agents inherit their parent's tags, so a tag drill-through wants them included.
      rootOnly: search.get("rootOnly") !== "false",
    };
  });
  return initial;
}

export default function InvocationLogPage({ params }) {
  const [isLoading, setIsLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalOpen, { open, close }] = useDisclosure(false);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursorHistory, setCursorHistory] = useState([]);

  const isMobile = useIsMobile();
  // Seven controls at full height pushed the first row of results off a phone screen entirely,
  // so on mobile they start folded away behind a count of what is currently applied.
  const [filtersOpen, { toggle: toggleFilters }] = useDisclosure(false);

  // Filters can be seeded from the query string so other pages (e.g. the tag cost report) can
  // link straight into a pre-filtered log. Read once at mount: the controls own the state after.
  const initialFilters = useInitialFiltersFromUrl();

  const [filterStatuses, setFilterStatuses] = useState(initialFilters.statuses);
  const [filterEnvironments, setFilterEnvironments] = useState([]);
  const [filterFlows, setFilterFlows] = useState(initialFilters.flows);
  const [filterTags, setFilterTags] = useState(initialFilters.tags);
  const [filterFrom, setFilterFrom] = useState(null);
  const [filterTo, setFilterTo] = useState(null);
  const [filterDateRange, setFilterDateRange] = useState(null);
  const [filterRootOnly, setFilterRootOnly] = useState(initialFilters.rootOnly);
  const [filterHasWarnings, setFilterHasWarnings] = useState(false);
  const [filterCancelled, setFilterCancelled] = useState(false);

  const [flows, setFlows] = useState([]);

  const [, setLocation] = useLocation();

  // Identity changes whenever a filter changes, so the polling interval always
  // re-captures the filters currently applied.
  const loadPage = useCallback(
    (overrideFilters = {}) => {
      const filter = {
        cursor,
        statuses: filterStatuses,
        environments: filterEnvironments,
        flows: filterFlows,
        tags: filterTags,
        from: filterFrom,
        to: filterTo,
        rootOnly: filterRootOnly || undefined, // Only send if true
        hasWarnings: filterHasWarnings || undefined,
        cancelled: filterCancelled || undefined,
        ...overrideFilters, // Allow overriding filters for immediate queries
      };
      getLlmMessageLogs(filter).then((data) => {
        setHasMore(data.hasMore);
        setMessages(data.data);
        setIsLoading(false);
      });
    },
    [
      cursor,
      filterStatuses,
      filterEnvironments,
      filterFlows,
      filterTags,
      filterFrom,
      filterTo,
      filterRootOnly,
      filterHasWarnings,
      filterCancelled,
    ]
  );

  useEffect(() => {
    setIsLoading(true);
    loadPage();
    // Filters are fetched explicitly via applyFilters / the date-range control; refetching here
    // would fire a request per keystroke in the TagsInputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor]);

  // Poll while a pending row is on screen so the row settles without a manual refresh.
  // loadPage is in the dependency list so a filter change replaces the interval.
  const hasRunningRows = messages.some((m) => m.status?.toLowerCase() === "pending");
  useEffect(() => {
    if (!hasRunningRows) return;
    const timer = setInterval(() => loadPage(), 5000);
    return () => clearInterval(timer);
  }, [hasRunningRows, cursor, loadPage]);

  // Stopping is cooperative, so the honest report is "asked N to stop", never "stopped N". The
  // rows settle on the next poll; jsonApiRequest already surfaces failures as a red notification.
  const reportStopped = (result) =>
    notifications.show({
      title: result.signalled === 0 ? "Nothing to stop" : "Stopping",
      message: result.message,
      color: result.signalled === 0 ? "gray" : "orange",
    });

  const confirmStopRun = (log) =>
    modals.openConfirmModal({
      title: "Stop this run?",
      children: (
        <Stack gap="xs">
          <Text size="sm">
            This stops {log.flowName || "the run"} and every sub-agent beneath it. It takes effect
            at the next step — a model call already in flight finishes first.
          </Text>
          <Text size="sm" c="dimmed">
            Whatever it has produced so far is kept and still billed. Stopping saves the work that
            has not happened yet.
          </Text>
        </Stack>
      ),
      labels: { confirm: "Stop run", cancel: "Keep running" },
      confirmProps: { color: "red" },
      onConfirm: () => cancelRun(log.id).then(reportStopped).then(() => loadPage()),
    });

  const confirmStopAll = () =>
    modals.openConfirmModal({
      title: "Stop everything that is running?",
      children: (
        <Stack gap="xs">
          <Text size="sm">
            This stops <b>every</b> run currently executing in this workspace — including runs
            started by other people and by your applications, and panel and bulk calls that no
            single run controls.
          </Text>
          <Text size="sm" c="dimmed">
            Each stops at its next step and keeps what it had already produced.
          </Text>
        </Stack>
      ),
      labels: { confirm: "Stop everything", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => cancelAllRuns().then(reportStopped).then(() => loadPage()),
    });

  useEffect(() => {
    getFlows().then((data) => {
      setFlows(data.map((p) => p.name));
    });
  }, []);

  useEffect(() => {
    if (params?.id) {
      getLlmMessageLog({ id: params.id }).then((data) => {
        setSelectedLog(data);
        open();
      });
    }
  }, [params.id, open]);

  const applyFilters = () => {
    setCursor(null);
    setCursorHistory([]);
    setIsLoading(true);
    loadPage({
      cursor: null,
      rootOnly: filterRootOnly || undefined,
      hasWarnings: filterHasWarnings || undefined,
      cancelled: filterCancelled || undefined,
    });
  };

  // Only counts filters that narrow the result set — `rootOnly` is on by default, so counting it
  // would show "1 filter" on a page nobody has touched.
  const activeFilterCount =
    filterStatuses.length +
    filterEnvironments.length +
    filterFlows.length +
    filterTags.length +
    (filterDateRange ? 1 : 0) +
    (filterHasWarnings ? 1 : 0) +
    (filterCancelled ? 1 : 0);

  const filterControls = (
    <>
      <Select
        label="Date range"
        placeholder="Any time"
        value={filterDateRange}
        onChange={(val) => {
          setFilterDateRange(val);
          if (val && val !== "custom") {
            const now = new Date();
            const ms = { "1h": 3600000, "24h": 86400000, "7d": 604800000 }[val];
            const from = new Date(now.getTime() - ms);
            setFilterFrom(from);
            setFilterTo(now);
            setCursor(null);
            setCursorHistory([]);
            setIsLoading(true);
            loadPage({ from, to: now, cursor: null });
          } else if (!val) {
            setFilterFrom(null);
            setFilterTo(null);
          }
        }}
        data={[
          { value: "1h", label: "Last Hour" },
          { value: "24h", label: "Last 24h" },
          { value: "7d", label: "Last 7d" },
          { value: "custom", label: "Custom Range" },
        ]}
        size="xs"
        clearable
        w={140}
      />
      {filterDateRange === "custom" && (
        <>
          <DateTimePicker
            label="From"
            placeholder="Start date/time"
            value={filterFrom}
            onChange={setFilterFrom}
            size="xs"
            clearable
            w={180}
          />
          <DateTimePicker
            label="To"
            placeholder="End date/time"
            value={filterTo}
            onChange={setFilterTo}
            size="xs"
            clearable
            w={180}
          />
        </>
      )}
      <TagsInput
        label="Flows"
        data={flows}
        value={filterFlows}
        onChange={setFilterFlows}
        size="xs"
        w={150}
      />
      <TagsInput
        label="Statuses"
        data={["succeeded", "failed", "pending"]}
        value={filterStatuses}
        onChange={setFilterStatuses}
        size="xs"
        w={150}
      />
      <TagsInput
        label="Environments"
        value={filterEnvironments}
        onChange={setFilterEnvironments}
        size="xs"
        w={150}
      />
      <TagsInput
        label="Tags"
        value={filterTags}
        onChange={setFilterTags}
        size="xs"
        w={150}
      />
      <Tooltip label="Show only runs that recorded a warning — failed tool calls, provider fallback, a forced conclusion. These are all 'succeeded'; that is the point.">
        <Switch
          label="Warnings only"
          checked={filterHasWarnings}
          onChange={(e) => setFilterHasWarnings(e.currentTarget.checked)}
          size="xs"
          mt={22}
        />
      </Tooltip>
      <Tooltip label="Show only runs that were stopped while executing. Like warnings, this cannot be a status: a run stopped after producing something still says 'succeeded'.">
        <Switch
          label="Stopped only"
          checked={filterCancelled}
          onChange={(e) => setFilterCancelled(e.currentTarget.checked)}
          size="xs"
          mt={22}
        />
      </Tooltip>
      <Tooltip label="Show only root-level requests (hide sub-agent calls)">
        <Switch
          label="Root only"
          checked={filterRootOnly}
          onChange={(e) => setFilterRootOnly(e.currentTarget.checked)}
          size="xs"
          mt={22}
        />
      </Tooltip>
    </>
  );

  const prevButton = (
    <Button
      onClick={() => {
        if (cursorHistory.length > 0) {
          const newHistory = [...cursorHistory];
          const previousCursor = newHistory.pop();
          setCursorHistory(newHistory);
          setCursor(previousCursor);
        }
      }}
      disabled={cursorHistory.length === 0}
      variant="default"
      size="xs"
    >
      Prev
    </Button>
  );

  const nextButton = (
    <Button
      onClick={() => {
        if (hasMore && messages.length > 0) {
          setCursorHistory([...cursorHistory, cursor]);
          setCursor(messages[messages.length - 1].id);
        }
      }}
      disabled={!hasMore}
      variant="default"
      size="xs"
    >
      Next
    </Button>
  );

  return (
    <Container fluid p={isMobile ? 0 : undefined}>
      <Stack justify="space-between" gap="lg">
        <Modal
          size="100%"
          fullScreen={isMobile}
          padding={isMobile ? "xs" : undefined}
          onClose={close}
          opened={modalOpen}
          title={<LogDetailHeader log={selectedLog} />}
          styles={{ title: { flex: 1, marginRight: "var(--mantine-spacing-md)", minWidth: 0 } }}
        >
          <InvocationLogDetails log={selectedLog} />
        </Modal>

        {/* Only offered when something is actually running: an always-visible emergency stop is
            either noise or a dare, and neither belongs above a list of finished runs. */}
        {hasRunningRows && (
          <Group justify="flex-end">
            <Button
              size="xs"
              color="red"
              variant="outline"
              leftSection={<IconPlayerStopFilled size={14} />}
              onClick={confirmStopAll}
            >
              Stop everything running
            </Button>
          </Group>
        )}

        {isMobile ? (
          <Stack gap="xs">
            <Group gap="xs" justify="space-between" wrap="nowrap">
              <Button
                size="xs"
                variant="default"
                leftSection={<IconFilter size={14} />}
                onClick={toggleFilters}
                rightSection={
                  activeFilterCount > 0 ? (
                    <Badge size="xs" circle variant="filled">
                      {activeFilterCount}
                    </Badge>
                  ) : null
                }
              >
                Filters
              </Button>
              <Group gap="xs" wrap="nowrap">
                {isLoading && <Loader size="xs" />}
                {prevButton}
                {nextButton}
              </Group>
            </Group>
            <Collapse in={filtersOpen}>
              <Stack gap="xs">
                <Group wrap="wrap" align="flex-end" gap="xs">
                  {filterControls}
                </Group>
                <Button size="xs" onClick={applyFilters}>
                  Filter
                </Button>
              </Stack>
            </Collapse>
          </Stack>
        ) : (
          <Group wrap="wrap" align="flex-end" gap="xs">
            {filterControls}
            <Button onClick={applyFilters} size="xs">
              Filter
            </Button>
            {prevButton}
            {nextButton}
            {isLoading && <Loader size="xs" />}
          </Group>
        )}

        {isMobile ? (
          <Stack gap="xs">
            {messages.map((p) => (
              <LogCard
                key={p.id}
                log={p}
                onOpen={() => setLocation("/logs/" + p.id)}
                onNavigateToParent={(parentId) =>
                  setLocation("/logs/" + parentId)
                }
                onStop={confirmStopRun}
              />
            ))}
          </Stack>
        ) : (
          <Table striped highlightOnHover style={{ cursor: "pointer" }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={30}></Table.Th>
                <Table.Th>Sent</Table.Th>
                <Table.Th>Flow</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Calls</Table.Th>
                <Table.Th>Environment</Table.Th>
                <Table.Th>Model</Table.Th>
                <Table.Th ta="right">Cost</Table.Th>
                <Table.Th ta="right">Milliseconds</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {messages.map((p) => (
                <Table.Tr
                  key={p.id}
                  onClick={() => {
                    setLocation("/logs/" + p.id);
                  }}
                  style={{ borderBottom: "none" }}
                >
                  <Table.Td>
                    <FeedbackIcon score={p.feedbackScore} />
                  </Table.Td>
                  <Table.Td>
                    <ReactTimeago date={p.sent} />
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <NameBadge name={p.flowName} />
                      <HierarchyBadge
                        log={p}
                        onNavigateToParent={(parentId) => setLocation("/logs/" + parentId)}
                      />
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <StatusBadge
                        status={p.status}
                        warnings={p.warnings}
                        warningCount={p.warningCount}
                        cancelledAt={p.cancelledAt}
                        cancelRequestedAt={p.cancelRequestedAt}
                        failureReason={p.failureReason}
                      />
                      <ProgressBadge
                        score={p.progressScore}
                        certainty={p.progressCertainty}
                        lowest={p.minProgressScore}
                        readings={p.progressScoreCount}
                        verifier={p.verifierModel}
                      />
                      <StopRunButton log={p} onStop={confirmStopRun} />
                    </Group>
                  </Table.Td>
                  <Table.Td ta="right">{p.stats?.totalResponses}</Table.Td>
                  <Table.Td>{p.environment}</Table.Td>
                  <Table.Td>{p.model}</Table.Td>
                  <Table.Td ta="right">
                    {p.childAgentCreditsUsed > 0 ? (
                      <Tooltip
                        label={
                          <Stack gap={2}>
                            <Text size="xs">Direct: {(p.creditsUsed / 10000).toFixed(4)}¢</Text>
                            <Text size="xs">Sub-agents: {(p.childAgentCreditsUsed / 10000).toFixed(4)}¢</Text>
                          </Stack>
                        }
                      >
                        <Text size="sm" fw={500}>
                          <NumberFormatter
                            suffix="¢"
                            decimalScale={2}
                            value={p.totalTreeCredits / 10000}
                          />
                        </Text>
                      </Tooltip>
                    ) : (
                      <NumberFormatter
                        suffix="¢"
                        decimalScale={2}
                        value={p.cost / 10000}
                      />
                    )}
                  </Table.Td>
                  <Table.Td ta="right">
                    <NumberFormatter thousandSeparator value={p.milliseconds} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Container>
  );
}
